import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { BotaoExportar } from "@/components/BotaoExportar";
import { ContaContabilSearchCombobox } from "@/components/ContaContabilSearchCombobox";
import { PaginacaoRodape } from "@/components/PaginacaoRodape";
import { usePaginacaoLocal } from "@/hooks/usePaginacaoLocal";
import { useRazao } from "@/features/contabil/hooks/useContabilizacao";
import { TIPO_OPERACAO_LABEL, type TipoOperacaoContabil } from "@/features/contabil/types/contabilizacao";

const fmtCur = (v: number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v);

export default function RazaoPage() {
  const [contaId, setContaId] = useState<number>(0);
  const [dtInicio, setDtInicio] = useState(new Date(new Date().getFullYear(), 0, 1).toISOString().slice(0, 10));
  const [dtFim, setDtFim] = useState(new Date().toISOString().slice(0, 10));

  const { data, isLoading } = useRazao(contaId, dtInicio, dtFim);
  const linhas = useMemo(() => data?.linhas ?? [], [data]);
  const pag = usePaginacaoLocal(linhas, 50);

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Livro Razão</h1>
        <p className="text-muted-foreground">Lançamentos por conta com saldo acumulado.</p>
      </div>

      <Card>
        <CardContent className="pt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <Label>Conta contábil</Label>
            <ContaContabilSearchCombobox
              value={contaId || null}
              onChange={(id) => setContaId(id ?? 0)}
              placeholder="Busque a conta por código ou nome..."
            />
          </div>
          <div>
            <Label>De</Label>
            <Input type="date" value={dtInicio} onChange={(e) => setDtInicio(e.target.value)} />
          </div>
          <div>
            <Label>Até</Label>
            <Input type="date" value={dtFim} onChange={(e) => setDtFim(e.target.value)} />
          </div>
        </CardContent>
      </Card>

      {!contaId ? (
        <p className="text-center text-muted-foreground py-8">Escolha uma conta para ver o razão.</p>
      ) : isLoading ? <Skeleton className="h-48" /> : !data ? null : (
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between gap-4">
              <CardTitle>{data.codigoConta} — {data.descricaoConta}</CardTitle>
              <BotaoExportar
                endpoint="/api/contabil/relatorios/razao/exportar"
                params={{ contaId, dtInicio, dtFim }}
              />
            </div>
            <p className="text-sm text-muted-foreground">
              Saldo inicial: <strong>{fmtCur(data.saldoInicial)}</strong> ·
              Total D: <strong>{fmtCur(data.totalDebitos)}</strong> ·
              Total C: <strong>{fmtCur(data.totalCreditos)}</strong> ·
              Saldo final: <strong className={data.saldoFinal >= 0 ? "text-green-600" : "text-red-600"}>
                {fmtCur(data.saldoFinal)}
              </strong>
            </p>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Nº lanç.</TableHead>
                  <TableHead>Origem</TableHead>
                  <TableHead>Documento</TableHead>
                  <TableHead>Pessoa</TableHead>
                  <TableHead>Contrapartida</TableHead>
                  <TableHead>Histórico</TableHead>
                  <TableHead className="text-right">Débito</TableHead>
                  <TableHead className="text-right">Crédito</TableHead>
                  <TableHead className="text-right">Saldo</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {linhas.length === 0 ? (
                  <TableRow><TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                    Nenhum lançamento no período.
                  </TableCell></TableRow>
                ) : pag.itensPaginados.map((l, i) => (
                  <TableRow key={`${l.lancamentoId}-${l.tipoOperacao}-${i}`}>
                    <TableCell>{new Date(l.dtMovimento).toLocaleDateString("pt-BR", { timeZone: "UTC" })}</TableCell>
                    <TableCell className="font-mono">{l.lancamentoId}</TableCell>
                    <TableCell className="text-xs">
                      {TIPO_OPERACAO_LABEL[l.tipoOperacao as TipoOperacaoContabil] ?? "—"}
                    </TableCell>
                    <TableCell className="text-xs whitespace-nowrap">{l.documento || "—"}</TableCell>
                    <TableCell className="text-xs">{l.pessoa || "—"}</TableCell>
                    <TableCell className="text-xs">{l.contrapartida || "—"}</TableCell>
                    <TableCell className="text-xs max-w-[16rem] truncate" title={l.historico ?? undefined}>
                      {l.historico || "—"}
                    </TableCell>
                    <TableCell className="text-right">{l.debito > 0 ? fmtCur(l.debito) : "—"}</TableCell>
                    <TableCell className="text-right">{l.credito > 0 ? fmtCur(l.credito) : "—"}</TableCell>
                    <TableCell className="text-right font-semibold">{fmtCur(l.saldo)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {linhas.length > 0 && (
              <PaginacaoRodape
                {...pag}
                onPaginaChange={pag.setPagina}
                onTamanhoPaginaChange={pag.setTamanhoPagina}
              />
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
